<template>
  <nav
    v-if="pages.length > 0 && !(hideWhenSinglePage && pageCount <= 1)"
    :aria-label="copy.nav"
    :class="cn('tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-4', $attrs.class as any)"
    :style="$attrs.style as any">
    <!-- Count line. Rendered first so `justify-between` splits it from the group; with no line the pager
         sits at the start, and callers who want it on the right pass `tw:justify-end` via class. -->
    <slot name="info">
      <p v-if="lineText != null" class="tw:m-0! tw:text-14! tw:text-gray-500">
        {{ lineText }}
      </p>
    </slot>

    <div role="group" class="tw:isolate tw:inline-flex tw:items-center">
      <LeadButton
        v-if="edges"
        variant="white"
        :icon="IconChevronsLeft"
        :aria-label="copy.first"
        :disabled="disabled || current <= 1"
        :class="edgeClasses(0)"
        data-testid="lead-pagination-first"
        @click="go(1)" />
      <LeadButton
        variant="white"
        :icon="IconChevronLeft"
        :aria-label="copy.prev"
        :disabled="disabled || current <= 1"
        :class="edgeClasses(edges ? 1 : 0)"
        data-testid="lead-pagination-prev"
        @click="go(current - 1)" />

      <template v-for="(p, i) in pages" :key="typeof p === 'number' ? `p-${p}` : `gap-${i}`">
        <!-- Ellipsis is a Button, NOT a span: ButtonGroup shapes children through `[&>button]` selectors
             (the -ml-px seam and the end radii), so a span between two buttons would break the chain. It
             is also NOT `disabled` — disabled:opacity-40 would fade the border and punch a hole in the
             outline. Inert-by-styling (pointer-events-none, tabindex -1, aria-hidden) keeps the outline
             unbroken. -->
        <LeadButton
          v-if="p === '…'"
          variant="white"
          :icon="IconDots"
          tabindex="-1"
          aria-hidden="true"
          :class="cn(groupClasses(edgeOffset + i), 'tw:pointer-events-none tw:text-gray-400!')" />
        <LeadButton
          v-else
          variant="white"
          :disabled="disabled"
          :aria-pressed="p === current ? true : undefined"
          :aria-current="p === current ? 'page' : undefined"
          :class="
            cn(
              groupClasses(edgeOffset + i),
              // digits: min-w-9 keeps single-digit segments as 36px squares matching the arrow
              // buttons; px-2 takes over from two digits up. tabular-nums keeps every digit on the
              // same advance so segment widths don't jitter as the page changes.
              'tw:min-w-9! tw:px-2! tw:tabular-nums',
              // selected tint (DS pattern): primary-lt fill, primary text, no raised shadow, above neighbours
              p === current && 'tw:relative tw:z-10 tw:bg-primary-lt! tw:text-primary! tw:shadow-none!'
            )
          "
          :data-testid="`lead-pagination-page-${p}`"
          @click="go(p as number)">
          {{ p }}
        </LeadButton>
      </template>

      <LeadButton
        variant="white"
        :icon="IconChevronRight"
        :aria-label="copy.next"
        :disabled="disabled || current >= pageCount"
        :class="edgeClasses(edgeOffset + pages.length)"
        data-testid="lead-pagination-next"
        @click="go(current + 1)" />
      <LeadButton
        v-if="edges"
        variant="white"
        :icon="IconChevronsRight"
        :aria-label="copy.last"
        :disabled="disabled || current >= pageCount"
        :class="edgeClasses(edgeOffset + pages.length + 1)"
        data-testid="lead-pagination-last"
        @click="go(pageCount)" />
    </div>
  </nav>
</template>

<script lang="ts">
export interface LeadPaginationLabels {
  /** The `<nav>`'s accessible name. */
  nav: string;
  first: string;
  prev: string;
  next: string;
  last: string;
  /** Connective word in the count line, e.g. English "of", German "von". */
  of: string;
}

/**
 * The page numbers to render, with `'…'` standing in for a hidden run.
 *
 * The slot count is constant — `siblingCount * 2 + 5` (first, last, current, both siblings, two ellipses) —
 * so a truncated pager keeps its width as the window slides. An ellipsis appears only when it actually
 * hides more than one page: swapping a single `2` for a `…` would make the pager WIDER while telling the
 * reader less.
 */
export function pageWindow(page: number, pageCount: number, siblingCount = 1): (number | '…')[] {
  if (pageCount < 1) return [];
  const range = (start: number, end: number) => Array.from({ length: end - start + 1 }, (_, i) => start + i);

  const current = Math.min(Math.max(page, 1), pageCount);
  const slots = siblingCount * 2 + 5;
  if (pageCount <= slots) return range(1, pageCount);

  const left = Math.max(current - siblingCount, 1);
  const right = Math.min(current + siblingCount, pageCount);
  const gapLeft = left > 2;
  const gapRight = right < pageCount - 1;

  if (!gapLeft) return [...range(1, Math.max(right, slots - 2)), '…', pageCount];
  if (!gapRight) return [1, '…', ...range(Math.min(left, pageCount - (slots - 3)), pageCount)];
  return [1, '…', ...range(left, right), '…', pageCount];
}
</script>

<script setup lang="ts">
// LeadPagination — mirrors the LEAD Design System <Pagination> component.
import { computed, useAttrs } from 'vue';
import { IconChevronLeft, IconChevronRight, IconChevronsLeft, IconChevronsRight, IconDots } from '@tabler/icons-vue';
import LeadButton from '@/components/lead-design/LeadButton.vue';
import translator from '@/locale/translator';
import { cn } from '@/lib/cn';

defineOptions({ inheritAttrs: false });

// Defaults come from the active translator so the count line and aria labels follow the app locale
// without every caller passing `labels`. German fallback strings match the design source's own defaults.
const localizedDefaults = (): LeadPaginationLabels => ({
  nav: (translator.get('pagination.nav') as string) || 'Seitennavigation',
  first: (translator.get('pagination.first') as string) || 'Erste Seite',
  prev: (translator.get('pagination.prev') as string) || 'Vorherige Seite',
  next: (translator.get('pagination.next') as string) || 'Nächste Seite',
  last: (translator.get('pagination.last') as string) || 'Letzte Seite',
  of: (translator.get('pagination.of') as string) || 'von',
});

const props = withDefaults(
  defineProps<{
    /** The current page, 1-based. Clamped into `[1, pageCount]`. */
    page: number;
    pageCount: number;
    /** Pages shown either side of the current one before the window truncates. */
    siblingCount?: number;
    /** First/last jump arrows outside the prev/next pair. */
    edges?: boolean;
    /** Total row count. Together with `pageSize` renders the "1–20 von 245" count line. */
    total?: number;
    pageSize?: number;
    /** Noun appended to the count line, e.g. `Anzeigen` → "1–20 von 245 Anzeigen". */
    itemLabel?: string;
    /** Replaces the generated count line. Pass `null` to suppress while still passing `total`. */
    info?: string | null;
    /** Overrides for the visible/assistive copy. Defaults to German. */
    labels?: Partial<LeadPaginationLabels>;
    /** Hide the whole component when everything fits on one page (pageCount <= 1). */
    hideWhenSinglePage?: boolean;
    /** Disable all buttons (e.g. while the table is loading) — the pager stays visible but inert. */
    disabled?: boolean;
  }>(),
  {
    siblingCount: 1,
    edges: true,
    total: undefined,
    pageSize: undefined,
    itemLabel: undefined,
    info: undefined,
    labels: undefined,
    hideWhenSinglePage: false,
    disabled: false,
  }
);

const emit = defineEmits<{
  (e: 'update:page', page: number): void;
  (e: 'change', page: number): void;
}>();

const attrs = useAttrs();
void attrs; // referenced in template via $attrs

/* --------------------------------- COMPUTED ------------------------------- */

const copy = computed<LeadPaginationLabels>(() => ({ ...localizedDefaults(), ...(props.labels ?? {}) }));

const current = computed(() => Math.min(Math.max(props.page, 1), Math.max(props.pageCount, 1)));

const pages = computed(() => pageWindow(props.page, props.pageCount, props.siblingCount));

const lineText = computed<string | null>(() => {
  if (props.info !== undefined) return props.info;
  if (props.total == null || props.pageSize == null || props.pageSize <= 0) return null;
  const from = props.total === 0 ? 0 : (current.value - 1) * props.pageSize + 1;
  const to = Math.min(current.value * props.pageSize, props.total);
  return `${from}–${to} ${copy.value.of} ${props.total}${props.itemLabel != null ? ` ${props.itemLabel}` : ''}`;
});

// Total segment count for radius maths: [first?] prev [pages…] next [last?]
const segmentCount = computed(() => pages.value.length + 2 + (props.edges ? 2 : 0));
const edgeOffset = computed(() => (props.edges ? 2 : 1));

/* --------------------------------- METHODS -------------------------------- */

function go(target: number) {
  const clamped = Math.min(Math.max(target, 1), Math.max(props.pageCount, 1));
  if (clamped === current.value) return;
  emit('update:page', clamped);
  emit('change', clamped);
}

// Group shape classes: collapse the child's own radius, restore only on the ends, overlap seams so
// joined boxes share one 1px stroke (not two), and lift on hover/focus so the neighbour's border
// doesn't paint over this button's stroke or clip its 4px focus ring.
function groupClasses(index: number): string {
  const last = segmentCount.value - 1;
  return cn(
    'tw:rounded-none!',
    index === 0 && 'tw:rounded-l-md!',
    index === last && 'tw:rounded-r-md!',
    index > 0 && 'tw:-ml-px!',
    'tw:hover:relative tw:hover:z-10 tw:focus-visible:relative tw:focus-visible:z-10'
  );
}

function edgeClasses(index: number): string {
  return groupClasses(index);
}
</script>
